import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type MigrationStatus = 'pending' | 'complete' | 'skipped';

export interface MigrationProgress {
  sequence_id: string;
  status: MigrationStatus;
  uploaded_at: string | null;
  uploaded_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface MigrationProgressStats {
  total: number;
  pending: number;
  complete: number;
  skipped: number;
  percentComplete: number;
}

export function useMigrationProgress() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all migration progress records
  const {
    data: progressRecords = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['migration-progress'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('migration_progress')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as MigrationProgress[];
    },
  });

  // Calculate progress stats
  const stats: MigrationProgressStats = {
    total: progressRecords.length,
    pending: progressRecords.filter((p) => p.status === 'pending').length,
    complete: progressRecords.filter((p) => p.status === 'complete').length,
    skipped: progressRecords.filter((p) => p.status === 'skipped').length,
    percentComplete: progressRecords.length > 0
      ? Math.round((progressRecords.filter((p) => p.status === 'complete').length / progressRecords.length) * 100)
      : 0,
  };

  // Create or update progress record
  const updateProgressMutation = useMutation({
    mutationFn: async ({
      sequenceId,
      status,
      notes,
    }: {
      sequenceId: string;
      status: MigrationStatus;
      notes?: string;
    }) => {
      const { data: session } = await supabase.auth.getSession();
      const userId = session.session?.user.id;

      const progressData: any = {
        sequence_id: sequenceId,
        status,
        uploaded_by: userId,
      };

      if (status === 'complete') {
        progressData.uploaded_at = new Date().toISOString();
      }

      if (notes) {
        progressData.notes = notes;
      }

      const { data, error } = await supabase
        .from('migration_progress')
        .upsert(progressData, {
          onConflict: 'sequence_id',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['migration-progress'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error updating progress',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Bulk initialize progress records for CSV import
  const initializeProgressMutation = useMutation({
    mutationFn: async (sequenceIds: string[]) => {
      const { data: session } = await supabase.auth.getSession();
      const userId = session.session?.user.id;

      const progressRecords = sequenceIds.map((id) => ({
        sequence_id: id,
        status: 'pending' as MigrationStatus,
        uploaded_by: userId,
      }));

      const { data, error } = await supabase
        .from('migration_progress')
        .upsert(progressRecords, {
          onConflict: 'sequence_id',
          ignoreDuplicates: true,
        })
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['migration-progress'] });
      toast({
        title: 'Progress initialized',
        description: `Added ${data.length} sequences to migration tracker`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error initializing progress',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete all progress records (reset)
  const resetProgressMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('migration_progress')
        .delete()
        .neq('sequence_id', '00000000-0000-0000-0000-000000000000'); // Delete all

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['migration-progress'] });
      toast({
        title: 'Progress reset',
        description: 'All migration progress has been cleared',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error resetting progress',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    progressRecords,
    stats,
    isLoading,
    error,
    updateProgress: updateProgressMutation.mutate,
    initializeProgress: initializeProgressMutation.mutate,
    resetProgress: resetProgressMutation.mutate,
    isUpdating: updateProgressMutation.isPending,
    isInitializing: initializeProgressMutation.isPending,
    isResetting: resetProgressMutation.isPending,
  };
}
